import React from 'react';
import { useDataFetching } from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import { TrendingUp, Brain, Filter, Target, Zap, DollarSign, Clock, CheckCircle, AlertTriangle } from 'lucide-react';

const Analytics = () => {
  const { data: analytics, loading, error, refetch } = useDataFetching(
    async () => {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics`);
      if (!response.ok) throw new Error('Failed to fetch analytics');
      return response.json();
    },
    []
  );

  if (loading) return <LoadingState message="Loading analytics..." />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const {
    ai_handling = {},
    noise_reduction = {},
    problem_management = {},
    prediction_accuracy = {},
    overall_performance = {},
    trends_7d = {}
  } = analytics || {};

  const MetricCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 ${color} rounded-lg`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">{title}</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">{value}</div>
          <div className="text-sm text-gray-600 mt-1">{subtitle}</div>
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              <span className="text-sm text-green-600">+{trend}% vs last week</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const SectionCard = ({ title, children, className = "" }) => (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">AI Analytics Dashboard</h1>
          <p className="mt-2 text-gray-600">
            Comprehensive performance metrics for Agentic AI across all operational areas
          </p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="AI Handling Rate"
            value={`${ai_handling.handling_rate || 0}%`}
            subtitle={`${ai_handling.incidents_handled || 0}/${ai_handling.total_incidents || 0} incidents`}
            icon={Brain}
            color="bg-blue-500"
            trend="5.2"
          />
          <MetricCard
            title="Noise Reduction"
            value={`${noise_reduction.reduction_rate || 0}%`}
            subtitle={`${((noise_reduction.events_filtered || 0) / 1000).toFixed(1)}k events filtered`}
            icon={Filter}
            color="bg-green-500"
            trend="3.8"
          />
          <MetricCard
            title="Prediction Accuracy"
            value={`${prediction_accuracy.accuracy || 0}%`}
            subtitle={`${prediction_accuracy.anomalies_detected || 0} anomalies detected`}
            icon={Target}
            color="bg-purple-500"
            trend="2.1"
          />
          <MetricCard
            title="Cost Savings"
            value={`$${((overall_performance.cost_savings_usd || 0) / 1000).toFixed(0)}k`}
            subtitle="This month"
            icon={DollarSign}
            color="bg-orange-500"
            trend="8.7"
          />
        </div>

        {/* Detailed Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* AI Incident Handling */}
          <SectionCard title="🤖 AI Incident Handling">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Incidents Handled</span>
                <span className="font-semibold">{ai_handling.incidents_handled || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Avg Resolution Time</span>
                <span className="font-semibold">{ai_handling.avg_resolution_time || 0} hrs</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Automated Resolutions</span>
                <span className="font-semibold text-green-600">{ai_handling.automated_resolutions || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Escalations</span>
                <span className="font-semibold text-orange-600">{ai_handling.escalations || 0}</span>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-gray-700">AI handling {ai_handling.handling_rate || 0}% of all incidents</span>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Noise Reduction */}
          <SectionCard title="🔇 Noise Reduction & Filtering">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Events Processed</span>
                <span className="font-semibold">{(noise_reduction.events_processed || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Events Filtered</span>
                <span className="font-semibold text-green-600">{(noise_reduction.events_filtered || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Filter Accuracy</span>
                <span className="font-semibold">{noise_reduction.accuracy || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Time Saved</span>
                <span className="font-semibold text-blue-600">{noise_reduction.time_saved_hours || 0} hrs</span>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-blue-500" />
                  <span className="text-sm text-gray-700">Reducing noise by {noise_reduction.reduction_rate || 0}% effectively</span>
                </div>
              </div>
            </div>
          </SectionCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Problem Management */}
          <SectionCard title="🔍 Problem Management & RCA">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Problems Identified</span>
                <span className="font-semibold">{problem_management.problems_identified || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Root Causes Found</span>
                <span className="font-semibold text-green-600">{problem_management.root_causes_found || 0}</span>
              </div>
              <div className="flex justify-between items-centers">
                <span className="text-gray-600">RCA Success Rate</span>
                <span className="font-semibold">{problem_management.rca_success_rate || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pattern Detection</span>
                <span className="font-semibold text-purple-600">{problem_management.pattern_detection_accuracy || 0}%</span>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-500" />
                  <span className="text-sm text-gray-700">Preventing {problem_management.prevention_rate || 0}% of recurring issues</span>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Prediction Performance */}
          <SectionCard title="🎯 Prediction & Detection">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Anomalies Detected</span>
                <span className="font-semibold">{prediction_accuracy.anomalies_detected || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">True Positives</span>
                <span className="font-semibold text-green-600">{prediction_accuracy.true_positives || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Precision</span>
                <span className="font-semibold">{prediction_accuracy.precision || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Early Detection</span>
                <span className="font-semibold text-blue-600">{prediction_accuracy.early_detection_minutes || 0} min avg</span>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  <span className="text-sm text-gray-700">Detecting issues {prediction_accuracy.early_detection_minutes || 0} minutes early</span>
                </div>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Overall Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <SectionCard title="⚡ Overall AI Performance" className="lg:col-span-2">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">AI Confidence</span>
                  <span className="font-semibold">{overall_performance.ai_confidence_avg || 0}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">System Uptime</span>
                  <span className="font-semibold text-green-600">{overall_performance.uptime || 0}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Response Time</span>
                  <span className="font-semibold">{overall_performance.response_time_ms || 0}ms</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Learning Rate</span>
                  <span className="font-semibold text-blue-600">+{overall_performance.learning_rate || 0}%/week</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Efficiency Gain</span>
                  <span className="font-semibold text-purple-600">{overall_performance.efficiency_improvement || 0}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cost Savings</span>
                  <span className="font-semibold text-green-600">${(overall_performance.cost_savings_usd || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="📊 AI Health Score">
            <div className="text-center">
              <div className="text-6xl font-bold text-green-500 mb-2">A+</div>
              <div className="text-lg font-semibold text-gray-700 mb-4">Excellent Performance</div>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Handling</span>
                  <span className="text-green-600">95%</span>
                </div>
                <div className="flex justify-between">
                  <span>Accuracy</span>
                  <span className="text-green-600">91%</span>
                </div>
                <div className="flex justify-between">
                  <span>Efficiency</span>
                  <span className="text-green-600">89%</span>
                </div>
                <div className="flex justify-between">
                  <span>Reliability</span>
                  <span className="text-green-600">97%</span>
                </div>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Bottom Summary */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <Brain className="h-8 w-8 text-blue-600 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Impact Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-blue-600 font-semibold">Incident Handling:</span>
                  <div className="text-gray-700">AI is managing 76.8% of all incidents with 4.2hr avg resolution</div>
                </div>
                <div>
                  <span className="text-green-600 font-semibold">Noise Reduction:</span>
                  <div className="text-gray-700">Filtering 68.9% of noise, saving 148 hours of manual work</div>
                </div>
                <div>
                  <span className="text-purple-600 font-semibold">Problem Management:</span>
                  <div className="text-gray-700">Found root causes in 75% of problems, preventing 34.5% recurrence</div>
                </div>
                <div>
                  <span className="text-orange-600 font-semibold">Prediction:</span>
                  <div className="text-gray-700">85% accuracy in anomaly detection, 23.5min early warning</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;